import { getUser } from 'shared/services/backend-api';
import { convertApnsToken } from 'shared/services/backend-api/convert-apns-token';
import { createPushToken } from 'shared/services/backend-api/create-push-token';
import { connectionHelper } from 'shared/services/backend-api/helpers/connection-helper';
import { getLoggableError, getRollbar } from 'shared/services/error-tracking';
import {
  getCachedApnsToken,
  setCachedApnsToken,
} from 'shared/services/local-storage/apns-token-cache';
import { isApnsSupported } from 'shared/utils/is-apns-supported';

export const subscribeToApnsTokenUpdate = () => {
  // Guard: only register listener on macOS Electron v2.3.0+
  if (!isApnsSupported()) {
    return;
  }

  const subscribeApnsToken = window.electron?.onPushTokenUpdate;

  if (!subscribeApnsToken || typeof subscribeApnsToken !== 'function') {
    return;
  }

  subscribeApnsToken(async (apnsToken) => {
    try {
      // Wake up Firebase connection
      connectionHelper().connect('APNs Token Update (IPC)');

      // Get current user
      const user = await getUser();
      if (!user) {
        return;
      }

      // Check if token has changed (skip redundant conversion)
      const cachedToken = getCachedApnsToken(user.uid);
      if (cachedToken === apnsToken) {
        return;
      }

      // Convert APNs token to FCM token via Cloud Function
      const fcmToken = await convertApnsToken(apnsToken);

      // Store FCM token in Firestore
      const locale = user.settings?.language;
      await createPushToken(user.uid, fcmToken, locale);

      // Cache APNs token for future comparison
      setCachedApnsToken(user.uid, apnsToken);
    } catch (error) {
      getRollbar().error(
        'Failed to process APNs token update',
        error as Error,
        {
          ...getLoggableError(error),
          tokenPreview: apnsToken?.substring(0, 8) || 'none',
        },
      );
    }
  });
};
